import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getIconFileName } from "../utils/iconUtils";
import { maskDriverName, isVisibleDriver, authHeaders } from "../utils/driverPrivacy";
import IracingPrivacyNotice from "./IracingPrivacyNotice";
import "../styles/PagesModern.css";
import RecentSessionsList from "./RecentSessionsList";
import Plot from "react-plotly.js";
import { LoadingPanel, PageHeader, PanelHeader } from "./RefreshUI";
import { CHART, chartAxis, chartBase } from "../utils/chartTheme";
import { LICENSE_COLORS } from "../utils/racingLabels";

// iRacing license level (1-20+) to class letter.
const licenseLevelLetter = (level) => {
        if (level >= 21) return "P";
        if (level >= 17) return "A";
        if (level >= 13) return "B";
        if (level >= 9) return "C";
        if (level >= 5) return "D";
        if (level >= 1) return "R";
        return "?";
};
const LETTER_COLORS = { P: LICENSE_COLORS.Pro, A: LICENSE_COLORS.A, B: LICENSE_COLORS.B, C: LICENSE_COLORS.C, D: LICENSE_COLORS.D, R: LICENSE_COLORS.Rookie };

// Lap times arrive in 1/10000 s; show m:ss.sss.
const formatLapTime = (raw) => {
        const seconds = raw / 10000;
        const minutes = Math.floor(seconds / 60);
        return `${minutes}:${(seconds % 60).toFixed(3).padStart(6, "0")}`;
};

const signed = (value, digits = 0) => `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(digits)}`;

// Width for a chart in one of two side-by-side panels on the wide data pages.
const halfPanelChartWidth = () => {
        const viewport = document.documentElement.clientWidth;
        if (viewport <= 800) return viewport - 50;
        const column = Math.min(viewport - 24, 1152);
        return Math.floor((column - 16) / 2 - 34);
};

const RaceResultsPage = () => {
        const [raceData, setRaceData] = useState(null);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState(null);
        const subsessionParam = useParams();
        const [recentSessions, setRecentSessions] = useState(null);
        const [recentSessionsLoading, setRecentSessionsLoading] = useState(true);
        const [percentilesLoading, setPercentilesLoading] = useState(true);
        const [percentiles, setPercentiles] = useState(null);
        const [percentilesQuali, setPercentilesQuali] = useState(null);
        const [searchId, setSearchId] = useState("");

        useEffect(() => {
                // Function to fetch race data
                const fetchRaceData = async () => {
                        setIsLoading(true);
                        try {
                                const response = await fetch(`https://iracing6-backend.herokuapp.com/api/sessionData/results/${subsessionParam.subsession_id}`, { headers: authHeaders() });
                                if (!response.ok) {
                                        throw new Error("Network response was not ok");
                                }
                                const data = await response.json();
                                setRaceData(data);
                                // Additional fetches or logic here...
                                fetchPercentiles(data.season_name, "QUALIFY");
                                fetchPercentiles(data.season_name, "RACE");
                        } catch (error) {
                                console.error("Error fetching data:", error);
                                setError("no subsession ID found");
                        } finally {
                                setIsLoading(false);
                        }
                };

                // Call the fetch function if subsessionParam.subsession_id is available
                if (subsessionParam.subsession_id) {
                        fetchRaceData();
                } else {
                        setIsLoading(false);
                }
        }, [subsessionParam.subsession_id]);

        // pull percentiles from backend

        const fetchPercentiles = async (season, type) => {
                try {
                        const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/series-car-percentiles/?seasonName=${season}&sessionType=${type}`);
                        if (type === "RACE") {
                                setPercentiles(response.data);
                        }
                        if (type === "QUALIFY") {
                                setPercentilesQuali(response.data);
                        }
                } catch (error) {
                        console.error("Error fetching recent sessions:", error);
                } finally {
                        setPercentilesLoading(false);
                }
        };

        const calculatePercentile = (value, carClass, sessionType) => {
                if (percentiles && sessionType === "RACE") {
                        // make sure carClass matches percentiles.car_class_name
                        let carClassIndex = percentiles[0].car_class_name;
                        if (carClassIndex !== carClass) {
                                return "error";
                        }

                        // make sure that sessionType matches percentiles.session_type
                        let sessionTypeIndex = percentiles[0].session_type;
                        if (sessionTypeIndex !== sessionType) {
                                return "error";
                        }

                        // find the percentiles.percentiles[] value that is closest to value
                        let closestPercentile = 0;
                        let closestPercentileIndex = 0;
                        let closestPercentileValue = 0;
                        for (let i = 0; i < percentiles[0].percentiles.length; i++) {
                                let currentPercentile = percentiles[0].percentiles[i];
                                if (Math.abs(currentPercentile - value) < Math.abs(closestPercentile - value)) {
                                        closestPercentile = currentPercentile;
                                        closestPercentileIndex = i;
                                        closestPercentileValue = currentPercentile;
                                }
                        }
                        return closestPercentileIndex;
                }
                if (percentilesQuali && sessionType === "QUALIFY") {
                        // make sure carClass matches percentiles.car_class_name
                        let carClassIndex = percentilesQuali[0].car_class_name;
                        if (carClassIndex !== carClass) {
                                return "error";
                        }

                        // make sure that sessionType matches percentiles.session_type
                        let sessionTypeIndex = percentilesQuali[0].session_type;
                        if (sessionTypeIndex !== sessionType) {
                                return "error";
                        }

                        // find the percentiles.percentiles[] value that is closest to value
                        let closestPercentile = 0;
                        let closestPercentileIndex = 0;
                        let closestPercentileValue = 0;
                        for (let i = 0; i < percentilesQuali[0].percentiles.length; i++) {
                                let currentPercentile = percentilesQuali[0].percentiles[i];
                                if (Math.abs(currentPercentile - value) < Math.abs(closestPercentile - value)) {
                                        closestPercentile = currentPercentile;
                                        closestPercentileIndex = i;
                                        closestPercentileValue = currentPercentile;
                                }
                        }
                        return closestPercentileIndex;
                }
        };

        const calculateLeaderboardPosition = (value, carClass, sessionType) => {
                if (sessionType === "RACE") {
                        if (percentiles) {
                                // make sure carClass matches percentiles.car_class_name
                                let carClassIndex = percentiles[0].car_class_name;
                                if (carClassIndex !== carClass) {
                                        return "error";
                                }

                                // calculate the closest percentiles[0].leaderboard to value, and return the index
                                let closestLeaderboard = 0;
                                let closestLeaderboardIndex = 0;
                                let closestLeaderboardValue = 0;
                                for (let i = 0; i < percentiles[0].leaderboard.length; i++) {
                                        let currentLeaderboard = percentiles[0].leaderboard[i];
                                        if (Math.abs(currentLeaderboard - value) < Math.abs(closestLeaderboard - value)) {
                                                closestLeaderboard = currentLeaderboard;
                                                closestLeaderboardIndex = i;
                                                closestLeaderboardValue = currentLeaderboard;
                                        }
                                }
                                return closestLeaderboardIndex + 1;
                        }
                }
                if (sessionType === "QUALIFY") {
                        if (percentilesQuali) {
                                // calculate the closest percentiles[0].leaderboard to value, and return the index
                                let closestLeaderboard = 0;
                                let closestLeaderboardIndex = 0;
                                let closestLeaderboardValue = 0;
                                for (let i = 0; i < percentilesQuali[0].leaderboard.length; i++) {
                                        let currentLeaderboard = percentilesQuali[0].leaderboard[i];
                                        if (Math.abs(currentLeaderboard - value) < Math.abs(closestLeaderboard - value)) {
                                                closestLeaderboard = currentLeaderboard;
                                                closestLeaderboardIndex = i;
                                                closestLeaderboardValue = currentLeaderboard;
                                        }
                                }
                                return closestLeaderboardIndex + 1;
                        }
                }
        };

        useEffect(() => {
                const fetchRecentSessions = async () => {
                        // todo: avoid if already on the results page
                        try {
                                const response = await axios.get(`https://iracing6-backend.herokuapp.com/api/sessionData/resultsrecent?limit=200`);
                                setRecentSessions(response.data);
                        } catch (error) {
                                console.error("Error fetching recent sessions:", error);
                        } finally {
                                setRecentSessionsLoading(false);
                        }
                };
                fetchRecentSessions();
        }, []);

        const navigate = useNavigate();

        const handleNameLink = (id) => {
                if (!isVisibleDriver(id)) return; // hidden drivers have no profile link
                navigate(`/user/careerstats/${id}`);
        };

        // Process the sessions data into a unique structure
        const uniqueSessionData = {};

        if (recentSessions) {
                recentSessions.forEach((session) => {
                        const key = `${session.start_time} - ${session.season_name}`;
                        if (!uniqueSessionData[key]) {
                                uniqueSessionData[key] = [];
                        }
                        uniqueSessionData[key].push(session);
                });
        }

        const formatTimeSince = (timeDiff) => {
                // Convert timeDiff from milliseconds to minutes, hours, days, etc.
                // and format it as a string like "2 hours ago", "3 days ago"
                // This is a basic example, you might want to handle different cases
                let minutes = Math.floor(timeDiff / 60000);
                let hours = Math.floor(minutes / 60);
                let days = Math.floor(hours / 24);

                if (days > 0) {
                        return `Started: ${days} day(s) ago`;
                } else if (hours > 0) {
                        return `Started: ${hours} hour(s) ago`;
                } else {
                        return `Started: ${minutes} minutes ago`;
                }
        };

        if (isLoading) {
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        <PageHeader title="Race results" />
                                        <LoadingPanel label="Loading race…" />
                                </main>
                        </div>
                );
        }

        if (error) {
                if (error === "no subsession ID found") {
                        return <RecentSessionsList setSearchId={setSearchId} searchId={searchId} />;
                }
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        <PageHeader title="Race results" />
                                        <section className="hp-panel pg-loading">
                                                <p className="hp-panel__note">This race couldn't load. Refresh the page to try again.</p>
                                        </section>
                                </main>
                        </div>
                );
        }

        if (!raceData) {
                return <RecentSessionsList setSearchId={setSearchId} searchId={searchId} />;
        }

        // The race session is usually third (practice, qualifying, race); look it up by name to be safe.
        const raceSession = raceData.results.find((session) => session.simsession_name === "RACE") || raceData.results[2];
        const qualifySession = raceData.results.find((session) => session.simsession_name === "QUALIFY");
        const raceResults = raceSession?.results || [];

        const bestLapNums = raceResults.filter((r) => r.best_lap_num !== -1).map((r) => r.best_lap_num);
        const incidentsNums = raceResults.filter((r) => r.incidents !== -1).map((r) => r.incidents);
        const numberOfLapsInTheRace = raceResults[0]?.laps_complete || 0;
        const seriesName = raceData.season_name.replace(/\s*-?\s*\d{4} Season(\s+\d+)?/, "").trim();
        const started = new Date(raceData.start_time);
        const chartWidth = halfPanelChartWidth();

        const countChart = (values, xTitle, xRange) => {
                const counts = values.reduce((acc, v) => {
                        acc[v] = (acc[v] || 0) + 1;
                        return acc;
                }, {});
                return (
                        <div className="hp-chart">
                                <Plot
                                        data={[{ x: Object.keys(counts).map(Number), y: Object.values(counts), type: "bar", width: 0.8, marker: { color: CHART.blue }, hovertemplate: `${xTitle} %{x}: %{y} drivers<extra></extra>` }]}
                                        layout={{
                                                ...chartBase(),
                                                margin: { t: 8, r: 8, l: 44, b: 44 },
                                                width: chartWidth,
                                                height: 230,
                                                bargap: 0.2,
                                                xaxis: { ...chartAxis(), title: { text: xTitle, font: { size: 12, color: CHART.muted } }, range: xRange, tick0: 1, showgrid: false },
                                                yaxis: { ...chartAxis(), title: { text: "Drivers", font: { size: 12, color: CHART.muted } } },
                                        }}
                                        config={{ displayModeBar: false }}
                                />
                        </div>
                );
        };

        const weekBadge = (result, sessionType) => {
                const pct = calculatePercentile(result.best_lap_time, result.car_class_name, sessionType);
                if (pct === undefined || pct === "error") return <span className="pg-week pg-week--none">–</span>;
                if (pct === 0) return <span className="pg-week pg-week--rank">Week #{calculateLeaderboardPosition(result.best_lap_time, result.car_class_name, sessionType)}</span>;
                return <span className={`pg-week${pct <= 5 ? " pg-week--top" : ""}`}>Top {pct}%</span>;
        };

        const resultsTable = (session, sessionType) => (
                <div className="pg-table-wrap">
                        <table className="pg-table pg-results">
                                <thead>
                                        <tr>
                                                <th className="pg-num">Pos</th>
                                                <th>Driver</th>
                                                <th className="pg-num">iRating</th>
                                                <th>Safety</th>
                                                <th className="pg-hide-sm">Car</th>
                                                <th className="pg-num">Fastest lap</th>
                                                <th>Week</th>
                                        </tr>
                                </thead>
                                <tbody>
                                        {session.results
                                                .filter((result) => result.best_lap_time !== -1)
                                                .map((result) => {
                                                        const visible = isVisibleDriver(result.cust_id);
                                                        const letter = licenseLevelLetter(result.new_license_level);
                                                        const irDelta = result.newi_rating - result.oldi_rating;
                                                        const srDelta = (result.new_sub_level - result.old_sub_level) / 100;
                                                        return (
                                                                <tr key={`${result.finish_position}-${result.cust_id ?? result.display_name}`}>
                                                                        <td className="pg-num pg-results__pos">{result.finish_position + 1}</td>
                                                                        <td>
                                                                                {visible ? (
                                                                                        <button type="button" className="pg-results__driver" onClick={() => handleNameLink(result.cust_id)}>
                                                                                                {maskDriverName(result.display_name)}
                                                                                        </button>
                                                                                ) : (
                                                                                        <span className="pg-muted">{maskDriverName(result.display_name)}</span>
                                                                                )}
                                                                        </td>
                                                                        <td className="pg-num">
                                                                                {result.newi_rating?.toLocaleString()}
                                                                                <span className={`pg-delta ${irDelta >= 0 ? "pg-delta--up" : "pg-delta--down"}`}>{signed(irDelta)}</span>
                                                                        </td>
                                                                        <td className="pg-nowrap">
                                                                                <span className="pg-lic" style={{ "--pg-lic": LETTER_COLORS[letter] }}>
                                                                                        {letter}
                                                                                </span>{" "}
                                                                                {(result.new_sub_level / 100).toFixed(2)}
                                                                                <span className={`pg-delta ${srDelta >= 0 ? "pg-delta--up" : "pg-delta--down"}`}>{signed(srDelta, 2)}</span>
                                                                        </td>
                                                                        <td className="pg-hide-sm pg-muted">{result.car_name}</td>
                                                                        <td className="pg-num">{formatLapTime(result.best_lap_time)}</td>
                                                                        <td>{weekBadge(result, sessionType)}</td>
                                                                </tr>
                                                        );
                                                })}
                                </tbody>
                        </table>
                </div>
        );

        return (
                <div className="pg">
                        <main className="hp-main">
                                <PageHeader title={seriesName} lede={`${raceData.track_name}, ${started.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`} />

                                <section className="pg-stats pg-stats--race" aria-label="Race summary">
                                        <div className="pg-stat">
                                                <span className="pg-stat__label">Strength of field</span>
                                                <span className="pg-stat__value">{raceData.event_strength_of_field?.toLocaleString()}</span>
                                        </div>
                                        <div className="pg-stat">
                                                <span className="pg-stat__label">Drivers</span>
                                                <span className="pg-stat__value">{raceResults.length}</span>
                                        </div>
                                        <div className="pg-stat">
                                                <span className="pg-stat__label">Laps</span>
                                                <span className="pg-stat__value">{numberOfLapsInTheRace}</span>
                                        </div>
                                        <div className="pg-stat">
                                                <span className="pg-stat__label">Subsession</span>
                                                <span className="pg-stat__value pg-stat__value--small">{subsessionParam.subsession_id}</span>
                                        </div>
                                </section>

                                <div className="pg-pair">
                                        <section className="hp-panel">
                                                <PanelHeader title="Best lap number" />
                                                <p className="hp-panel__note">Which lap each driver set their fastest time on.</p>
                                                {countChart(bestLapNums, "Lap", [0.5, numberOfLapsInTheRace + 0.5])}
                                        </section>
                                        <section className="hp-panel">
                                                <PanelHeader title="Incidents" />
                                                <p className="hp-panel__note">Incident points per driver in the race.</p>
                                                {countChart(incidentsNums, "Incident points", [-0.5, Math.max(0, ...incidentsNums) + 0.5])}
                                        </section>
                                </div>

                                {raceSession && (
                                        <section className="hp-panel">
                                                <PanelHeader title="Race" />
                                                <IracingPrivacyNotice />
                                                {resultsTable(raceSession, "RACE")}
                                        </section>
                                )}

                                {qualifySession && (
                                        <section className="hp-panel">
                                                <PanelHeader title="Qualifying" />
                                                {resultsTable(qualifySession, "QUALIFY")}
                                        </section>
                                )}

                                <p className="pg-fine">Week shows how each fastest lap ranks against every lap set in this series and car class this week.</p>
                        </main>
                </div>
        );
};

export default RaceResultsPage;
